import { useEffect, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { Lock, Loader2, ShieldCheck, CheckCircle2, AlertTriangle } from "lucide-react";
import Navbar from "../components/Navbar";
import Footer from "../components/Footer";
import SEO from "../components/SEO";
import { resetPassword, formatApiError } from "../lib/authApi";
import { useAuth } from "../context/AuthContext";
import { trackEvent } from "../lib/analytics";

export default function ResetPassword() {
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const { setUser } = useAuth();
  const token = params.get("token") || "";
  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const [done, setDone] = useState(false);

  useEffect(() => {
    if (!token) setError("This reset link is missing its token. Please request a new one.");
  }, [token]);

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    if (password.length < 8) {
      setError("Your new password must be at least 8 characters long.");
      return;
    }
    if (password !== confirm) {
      setError("The two passwords don't match.");
      return;
    }
    setBusy(true);
    try {
      const res = await resetPassword(token, password);
      setUser(res.user);
      trackEvent("password_reset_complete", { method: "email_link" });
      setDone(true);
      setTimeout(() => navigate("/dashboard", { replace: true }), 1600);
    } catch (err) {
      setError(formatApiError(err));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="min-h-screen bg-white flex flex-col">
      <SEO
        path="/reset-password"
        title="Reset your password"
        description="Set a new password for your KnowYourPolicy.ai account."
        noindex
      />
      <Navbar />
      <div className="flex-1 max-w-md w-full mx-auto px-4 sm:px-6 pt-14 pb-16" data-testid="reset-page">
        <div className="text-center">
          <div className="mx-auto h-12 w-12 rounded-2xl bg-blue-600 text-white flex items-center justify-center shadow-lg shadow-blue-600/20">
            <ShieldCheck className="h-5 w-5" />
          </div>
          <h1 className="mt-5 font-display text-2xl md:text-3xl tracking-tight text-slate-900" data-testid="reset-title">
            Choose a new password
          </h1>
          <p className="mt-2 text-sm text-slate-600">
            Pick something you'll remember — you'll be signed in automatically after this.
          </p>
        </div>

        {done ? (
          <div
            className="mt-8 border border-emerald-200 bg-emerald-50 rounded-2xl p-5 text-sm text-emerald-800 flex items-start gap-2"
            data-testid="reset-success"
          >
            <CheckCircle2 className="h-4 w-4 mt-0.5 shrink-0" />
            <span>Password updated. Taking you to your dashboard…</span>
          </div>
        ) : !token ? (
          <div
            className="mt-8 border border-red-200 bg-red-50 rounded-2xl p-5 text-sm text-red-700 flex items-start gap-2"
            data-testid="reset-no-token"
          >
            <AlertTriangle className="h-4 w-4 mt-0.5 shrink-0" />
            <div>
              This reset link is missing its token or has been altered.{" "}
              <Link to="/ask" className="underline underline-offset-2 font-medium">
                Request a new one
              </Link>
              .
            </div>
          </div>
        ) : (
          <form onSubmit={submit} className="mt-8 space-y-3" data-testid="reset-form">
            <label className="block">
              <span className="text-xs font-medium text-slate-600">New password</span>
              <div className="mt-1 flex items-center gap-2 bg-slate-50 border border-slate-200 rounded-xl px-3 py-2.5 focus-within:border-blue-400 focus-within:bg-white">
                <Lock className="h-4 w-4 text-slate-400" />
                <input
                  type="password"
                  required
                  minLength={8}
                  autoFocus
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  autoComplete="new-password"
                  className="flex-1 bg-transparent outline-none text-sm text-slate-800 placeholder:text-slate-400"
                  placeholder="At least 8 characters"
                  data-testid="reset-password"
                />
              </div>
            </label>
            <label className="block">
              <span className="text-xs font-medium text-slate-600">Confirm new password</span>
              <div className="mt-1 flex items-center gap-2 bg-slate-50 border border-slate-200 rounded-xl px-3 py-2.5 focus-within:border-blue-400 focus-within:bg-white">
                <Lock className="h-4 w-4 text-slate-400" />
                <input
                  type="password"
                  required
                  minLength={8}
                  value={confirm}
                  onChange={(e) => setConfirm(e.target.value)}
                  autoComplete="new-password"
                  className="flex-1 bg-transparent outline-none text-sm text-slate-800 placeholder:text-slate-400"
                  placeholder="Type it again"
                  data-testid="reset-password-confirm"
                />
              </div>
            </label>

            {error && (
              <div
                className="text-sm text-red-700 bg-red-50 border border-red-100 rounded-lg px-3 py-2"
                data-testid="reset-error"
              >
                {error}
              </div>
            )}

            <button
              type="submit"
              disabled={busy}
              data-testid="reset-submit"
              className="w-full inline-flex items-center justify-center gap-2 bg-blue-600 hover:bg-blue-700 disabled:bg-slate-300 text-white rounded-full py-2.5 text-sm font-medium shadow-sm"
            >
              {busy && <Loader2 className="h-4 w-4 animate-spin" />}
              Update password
            </button>
          </form>
        )}
      </div>
      <Footer />
    </div>
  );
}
